<template>
  <div class="geocode-picker">
    <v-text-field
      v-model="query"
      :label="label"
      :placeholder="placeholder"
      variant="outlined"
      density="comfortable"
      :loading="loading"
      clearable
      @click:clear="clear"
      @focus="openMenu = predictions.length > 0"
      @blur="onBlur"
      @input="onInput"
    >
      <template #prepend-inner>
        <v-icon>mdi-map-search</v-icon>
      </template>
    </v-text-field>

    <!-- Inline results under the input (better mobile UX) -->
    <div v-if="openMenu" class="results-panel">
      <v-list density="comfortable">
        <v-list-item
          v-for="p in predictions"
          :key="p.place_id"
          @click="selectPrediction(p)"
        >
          <template #prepend>
            <v-icon color="primary">mdi-map-marker</v-icon>
          </template>
          <v-list-item-title>{{ p.description }}</v-list-item-title>
          <v-list-item-subtitle
            v-if="
              p.structured_formatting && p.structured_formatting.secondary_text
            "
          >
            {{ p.structured_formatting.secondary_text }}
          </v-list-item-subtitle>
        </v-list-item>
        <v-list-item
          v-if="!predictions.length && query.length >= minChars && !loading"
        >
          <v-list-item-title>Geen resultaten</v-list-item-title>
        </v-list-item>
      </v-list>
    </div>

    <!-- Selected locations table (set primary, remove) -->
    <div v-if="multiple && Array.isArray(model) && model.length" class="mt-2">
      <v-table density="comfortable">
        <thead>
          <tr>
            <th>Locatie</th>
            <th class="text-center" style="width: 72px">Primair</th>
            <th class="text-right" style="width: 72px">Acties</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sel in model" :key="sel.place_id">
            <td>{{ display(sel) }}</td>
            <td class="text-center">
              <v-btn
                size="small"
                variant="text"
                :color="primaryId === sel.place_id ? 'amber-darken-3' : 'grey'"
                icon
                @click="setPrimary(sel.place_id)"
              >
                <v-icon>{{
                  primaryId === sel.place_id ? "mdi-star" : "mdi-star-outline"
                }}</v-icon>
              </v-btn>
            </td>
            <td class="text-right">
              <v-btn
                size="small"
                variant="text"
                color="red"
                icon
                @click="removeSelected(sel.place_id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>

    <div v-if="debug && debugPayload" class="mt-2 text-caption">
      <strong>Geselecteerd:</strong>
      <pre style="white-space: pre-wrap">{{
        JSON.stringify(debugPayload, null, 2)
      }}</pre>
    </div>
  </div>
</template>

<script setup>
/* global google */
defineOptions({ name: "GeocodePicker" });
import { ref, computed, watch, onMounted } from "vue";

const props = defineProps({
  modelValue: { type: [Object, Array], default: () => ({}) },
  label: { type: String, default: "Zoek locatie" },
  placeholder: {
    type: String,
    default: "Straat/Huisnummer/Stad of land/gebied",
  },
  debounceMs: { type: Number, default: 350 },
  minChars: { type: Number, default: 3 },
  multiple: { type: Boolean, default: true },
  debug: { type: Boolean, default: true },
  primaryPlaceId: { type: String, default: null },
});

const emit = defineEmits([
  "update:modelValue",
  "selected",
  "update:primaryPlaceId",
]);

const query = ref("");
const loading = ref(false);
const predictions = ref([]);
const openMenu = ref(false);
const sessionTokenRef = ref(null);

// Internal state to support standalone usage without v-model
const internalValue = ref(
  props.multiple
    ? Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : []
    : props.modelValue || {},
);

const model = computed({
  get: () => internalValue.value,
  set: (val) => {
    internalValue.value = val;
    emit("update:modelValue", val);
  },
});

// Track primary place id locally and sync with parent via v-model:primaryPlaceId
const primaryId = ref(null);

function display(sel) {
  const text = sel.formatted_address || sel.name || sel.description;
  return text && text.trim().length > 0 ? text : "Locatie wordt bijgewerkt…";
}

function setPrimary(placeId) {
  primaryId.value = placeId;
  emit("update:primaryPlaceId", placeId);
}

const debugPayload = computed(() => {
  if (props.multiple)
    return Array.isArray(internalValue.value) ? internalValue.value : [];
  return internalValue.value;
});

let debounceTimer = null;

function onInput() {
  if (!mapsReady.value) return;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(runPrediction, props.debounceMs);
}

function clear() {
  query.value = "";
  predictions.value = [];
  openMenu.value = false;
  // Only clear the search input; keep previously selected locations intact
}

function onBlur() {
  setTimeout(() => (openMenu.value = false), 150);
}

const mapsReady = ref(false);

async function ensureMapsLoaded() {
  if (window.google && window.google.maps && window.google.maps.places) {
    mapsReady.value = true;
    return;
  }
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.warn("VITE_GOOGLE_MAPS_API_KEY niet geconfigureerd");
    return;
  }
  await new Promise((resolve, reject) => {
    const scriptId = "gmaps-places-script";
    if (document.getElementById(scriptId)) {
      return resolve();
    }
    const s = document.createElement("script");
    s.id = scriptId;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly&loading=async`;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  try {
    if (google?.maps?.importLibrary) {
      await google.maps.importLibrary("places");
    }
  } catch (e) {
    // ignore, fallback to window.google.maps.places if present
  }
  mapsReady.value = true;
}

function newSessionToken() {
  try {
    sessionTokenRef.value = new google.maps.places.AutocompleteSessionToken();
  } catch (e) {
    sessionTokenRef.value = null;
  }
}

async function runPrediction() {
  const q = query.value.trim();
  if (q.length < props.minChars) {
    predictions.value = [];
    openMenu.value = false;
    return;
  }
  loading.value = true;
  try {
    if (!sessionTokenRef.value) newSessionToken();
    const service = new google.maps.places.AutocompleteService();
    const request = {
      input: q,
      sessionToken: sessionTokenRef.value,
      types: ["geocode", "establishment"],
    };
    await new Promise((resolve) => {
      service.getPlacePredictions(request, (preds) => {
        predictions.value = Array.isArray(preds) ? preds : [];
        openMenu.value = predictions.value.length > 0;
        resolve();
      });
    });
  } catch (e) {
    console.error("Autocomplete error", e);
    predictions.value = [];
  } finally {
    loading.value = false;
  }
}

async function selectPrediction(p) {
  openMenu.value = false;
  query.value = ""; // clear to allow subsequent selections

  try {
    let result = null;

    // Prefer new Places SDK (Place) when available
    if (google?.maps?.places?.Place) {
      const place = new google.maps.places.Place({ id: p.place_id });
      try {
        await place.fetchFields({
          fields: [
            "id",
            "displayName",
            "formattedAddress",
            "location",
            "types",
            "addressComponents",
          ],
        });

        const loc = place?.location;
        const lat =
          typeof loc?.lat === "function" ? loc.lat() : (loc?.lat ?? null);
        const lng =
          typeof loc?.lng === "function" ? loc.lng() : (loc?.lng ?? null);

        result = {
          place_id: place?.id || p.place_id,
          description: p.description,
          formatted_address: place?.formattedAddress || p.description || "",
          lat,
          lng,
          types: place?.types || [],
          name: place?.displayName?.text || null,
          address_components: place?.addressComponents || [],
        };
      } catch (e) {
        // If new API fails, fall back to legacy below
        console.warn(
          "Place.fetchFields failed, falling back to PlacesService",
          e,
        );
      }
    }

    // Legacy fallback using PlacesService
    if (!result) {
      const mapDiv = document.createElement("div");
      const places = new google.maps.places.PlacesService(mapDiv);
      const details = await new Promise((resolve) => {
        places.getDetails(
          {
            placeId: p.place_id,
            fields: [
              "place_id",
              "formatted_address",
              "geometry.location",
              "address_components",
              "types",
              "name",
            ],
            sessionToken: sessionTokenRef.value,
          },
          (res) => resolve(res),
        );
      });

      const loc = details?.geometry?.location;
      const lat = typeof loc?.lat === "function" ? loc.lat() : null;
      const lng = typeof loc?.lng === "function" ? loc.lng() : null;

      result = {
        place_id: details?.place_id || p.place_id,
        description: p.description,
        formatted_address: details?.formatted_address || p.description || "",
        lat,
        lng,
        types: details?.types || [],
        name: details?.name || null,
        address_components: details?.address_components || [],
      };
    }

    if (props.multiple) {
      const current = Array.isArray(internalValue.value)
        ? [...internalValue.value]
        : [];
      const exists = current.some((x) => x.place_id === result.place_id);
      if (!exists) current.push(result);
      internalValue.value = current;
      emit("update:modelValue", internalValue.value);
      emit("selected", result);
      // If no primary chosen yet, set the first selection as primary
      if (!primaryId.value && internalValue.value.length > 0) {
        primaryId.value = internalValue.value[0].place_id;
        emit("update:primaryPlaceId", primaryId.value);
      }
    } else {
      internalValue.value = result;
      emit("update:modelValue", internalValue.value);
      emit("selected", result);
    }

    predictions.value = [];
    newSessionToken();
  } catch (e) {
    console.error("Place details error", e);
  }
}

function removeSelected(placeId) {
  if (!props.multiple) return;
  const current = Array.isArray(internalValue.value)
    ? [...internalValue.value]
    : [];
  const next = current.filter((x) => x.place_id !== placeId);
  internalValue.value = next;
  emit("update:modelValue", internalValue.value);
  // If removed was primary, auto-select a new primary
  if (primaryId.value === placeId) {
    const newPrimary = next[0]?.place_id || null;
    primaryId.value = newPrimary;
    emit("update:primaryPlaceId", newPrimary);
  }
}

onMounted(async () => {
  await ensureMapsLoaded();
  newSessionToken();
  // Initialize primary from parent if provided
  if (props.multiple) {
    primaryId.value = props.primaryPlaceId || primaryId.value;
  }
});

watch(
  () => props.modelValue,
  (val) => {
    // Sync down from parent when v-model is bound
    if (props.multiple) {
      const arr = Array.isArray(val) ? [...val] : [];
      internalValue.value = arr;
      if (arr.length) {
        // Prefer parent's primary if valid
        const parentPrimary = props.primaryPlaceId;
        if (parentPrimary && arr.find((x) => x.place_id === parentPrimary)) {
          primaryId.value = parentPrimary;
        } else if (
          !primaryId.value ||
          !arr.find((x) => x.place_id === primaryId.value)
        ) {
          // Fallback: ensure some primary exists locally
          primaryId.value = arr[0]?.place_id || null;
          // Only emit if parent did not specify a primary
          if (!parentPrimary) emit("update:primaryPlaceId", primaryId.value);
        }
      }
    } else {
      internalValue.value = val || {};
      if (
        val &&
        val.formatted_address &&
        val.formatted_address !== query.value
      ) {
        query.value = val.formatted_address;
      }
    }
  },
  { deep: true },
);

// Keep local primaryId in sync if parent controls it via v-model:primaryPlaceId
watch(
  () => props.primaryPlaceId,
  (val) => {
    primaryId.value = val || primaryId.value;
  },
);
</script>

<style scoped>
.geocode-picker {
  position: relative;
}
.results-panel {
  margin-top: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  max-height: 320px;
  overflow-y: auto;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
</style>
